import React from "react";
import parse, { type DOMNode, type Element, type HTMLReactParserOptions } from "html-react-parser-v6";
import { Tooltip } from "../tooltip/tooltip.client";
import { isTooltipPlacement } from "../tooltip/tooltip.shared";
import { hasMarkup, needsBlockHost, normalizeTooltipTags, PHRASING } from "./text.shared";
import { tooltipPanel } from "./text.tooltip";
import { withSafeRender } from "../safe-render";

export { hasMarkup, needsBlockHost, PHRASING };
export {
  TEXT_BODY_ELEMENTS,
  TEXT_BODY_VARIANTS,
  TEXT_HEADING_VARIANTS,
  TEXT_LEVELS
} from "./text.shared";
export type {
  TextBodyElement,
  TextBodyVariant,
  TextHeadingVariant,
  TextLevel
} from "./text.shared";

/* The single source of truth for the type scale — the union, the spec's
   stylesheet sweep and the stories' control all derive from this array, so the
   three cannot drift apart by hand.

   IT DOES NOT CLOSE THE `label` REGRESSION. Those callers were
   atoms/price/price.tsx and molecules/price-block/price-block.tsx, which appear
   in none of the three lists; a variant leaving the union is a type error at
   the call site, and next.config.js sets typescript.ignoreBuildErrors. The gate
   that would have caught it is `tsc --noEmit` in CI. text.spec.tsx pins the
   stylesheet in both directions, which is the part this array does buy. */
export const TEXT_VARIANTS = [
  "headline-1",
  "headline-2",
  "headline-3",
  "headline-1-cmp",
  "headline-2-cmp",
  "headline-3-cmp",
  "subhead-1",
  "subhead-1-cmp",
  "subhead-2",
  "body-1",
  "body-2",
  "body-3",
  "label",
  "eyebrow",
  "text-link-1",
  "text-link-2",
  "text-link-3",
  "number-1",
  "number-2",
  "number-strikethrough"
] as const;

export type TextVariant = (typeof TEXT_VARIANTS)[number];

/* The same ramp as a keyed set, for the CMS boundaries that check membership
   with hasOwnProperty. Derived, so a variant cannot reach one shape only. */
export const TEXT_VARIANT_SET = Object.fromEntries(
  TEXT_VARIANTS.map(variant => [variant, true])
) as Record<TextVariant, true>;

export type TextTone =
  | "primary"
  | "secondary"
  | "muted"
  | "tertiary"
  | "disabled"
  | "inverse";
export type TextWeight = "regular" | "medium" | "semibold" | "bold";
export type TextElement =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "p"
  | "span"
  | "div";

export const TEXT_ELEMENTS: Record<TextElement, true> = {
  h1: true,
  h2: true,
  h3: true,
  h4: true,
  h5: true,
  h6: true,
  p: true,
  span: true,
  div: true
};

const HEADINGS: Partial<Record<TextElement, true>> = {
  h1: true,
  h2: true,
  h3: true,
  h4: true,
  h5: true,
  h6: true
};

function allowedUnder(element: TextElement, parent?: TextElement): TextElement {
  if (parent === "p") {
    return "span";
  }
  if (parent !== undefined && HEADINGS[parent] && HEADINGS[element]) {
    return "span";
  }
  return element;
}

/**
 * A CMS-chosen element from `allowed`, narrowed to one React can hydrate under
 * `parent`. React's validateDOMNesting rejects a heading inside a heading, and
 * a div, p or heading inside an open p; anything off `allowed` takes `fallback`.
 */
export function safeElement<T extends TextElement>(
  asked: unknown,
  fallback: T,
  allowed: Record<T, true>
): T;
export function safeElement<T extends TextElement>(
  asked: unknown,
  fallback: T,
  allowed: Record<T, true>,
  parent: TextElement | undefined
): TextElement;
export function safeElement<T extends TextElement>(
  asked: unknown,
  fallback: T,
  allowed: Record<T, true>,
  parent?: TextElement
): TextElement {
  const element =
    typeof asked === "string" &&
    Object.prototype.hasOwnProperty.call(allowed, asked) ?
      (asked as T) :
      fallback;
  return allowedUnder(element, parent);
}

/** A heading's outline rank, 1-6; undefined for anything that is not one. */
export function headingRank(element: TextElement): number | undefined {
  return HEADINGS[element] ? Number(element.slice(1)) : undefined;
}

/** A CMS-chosen variant from `allowed`, or `fallback` for anything else. */
export function safeVariant<T extends TextVariant>(
  asked: unknown,
  fallback: T,
  allowed: Record<T, true>
): T {
  return typeof asked === "string" &&
    Object.prototype.hasOwnProperty.call(allowed, asked) ?
    (asked as T) :
    fallback;
}

export interface TextProps {
  /** Wrapping element, chosen independently of `variant`. */
  as?: TextElement;
  variant?: TextVariant;
  tone?: TextTone;
  /** Unset keeps the weight the variant defines. */
  weight?: TextWeight;
  id?: string;
  className?: string;
  /**
   * Opt-in hook into the markup parse, html-react-parser's own `replace`: return
   * an element to draw in place of a parsed node, or undefined to keep it. Used
   * only when `children` is markup. Text's own `<tooltip>` handling runs first and
   * the hook sees every other node, so it cannot override the tooltip. Trusted
   * input: what it returns is drawn unfiltered. With none, Text parses as before.
   */
  replace?: HTMLReactParserOptions["replace"];
  children: React.ReactNode;
}

/**
 * The one extra function: CMS markup to React elements. `html-react-parser-v6`
 * is the npm alias for html-react-parser 6, running beside the app's 3 until
 * Fusion and Video-Lab can move too. It parses rather than injecting, so this
 * works in Node and rich text is in the server HTML.
 *
 * ⚠ NOTHING IS FILTERED HERE. React drops string event handlers — onclick,
 * onerror, onload never become listeners — but it does NOT stop a <script>
 * element reaching the server HTML, where the browser will run it, and it only
 * warns on a javascript: href. Rich-text children are therefore TRUSTED INPUT,
 * and whatever fills them owns that trust. Recorded on Design conflicts → Text.
 */
function parseHtml(html: string): React.ReactNode {
  return parse(normalizeTooltipTags(html));
}

const TOOLTIP_TAG = /<tooltip(?![\w-])/i;

/* Elements that cannot hold another control. Only the copy is visible from
   here: a Link or Button component wrapped around Text is the caller's to keep
   clear of <tooltip> copy. */
const INTERACTIVE = new Set(["a", "button", "label"]);

/* The CMS tag `<tooltip aria-label placement>body</tooltip>` drawn as the
   Tooltip, inline where it sits. The name is `aria-label` first because that
   is what production reads. Fusion's `mode` is not read: pending PO/UX
   confirmation; DDS Tooltip has no per-instance light/dark variant. The body
   is sanitized here, on the server, and handed over as the Tooltip's children. */
function replaceTooltip(node: DOMNode): React.ReactElement | undefined {
  if (node.type !== "tag" || node.name !== "tooltip") {
    return undefined;
  }
  let ancestor = node.parent;
  while (ancestor) {
    /* A button inside a link or button is nested interactive content, so the
       tag draws nothing there — which also keeps it out of plainText. */
    if (INTERACTIVE.has((ancestor as Element).name)) {
      return <></>;
    }
    ancestor = ancestor.parent;
  }
  // A malformed tag costs only its own tooltip, not the copy around it.
  let panel: ReturnType<typeof tooltipPanel>;
  try {
    panel = tooltipPanel(node);
  } catch (error) {
    console.error("[DDS] Text tooltip failed", error);
    return <></>;
  }
  if (!panel) {
    return <></>;
  }
  const { attribs } = node;
  const label = attribs["aria-label"]?.trim() || attribs.label?.trim();
  // The CMS writes the value however it likes: "Top" is top.
  const placement = attribs.placement?.toLowerCase();
  return (
    <Tooltip
      containsLink={panel.hasLink}
      label={label || undefined}
      placement={isTooltipPlacement(placement) ? placement : undefined}
    >
      {panel.node}
    </Tooltip>
  );
}

/* A `<tooltip>` in a LABEL draws nothing at all. A label is nested interactive
   content, which the tag already refuses to draw inside (see replaceTooltip),
   and `plainText` reads the tag as no words — so a label that drew the pop-up
   body inline would say one thing on screen and a different thing to a screen
   reader, which is the disagreement plainText exists to prevent (WCAG 2.5.3).
   Text is the component that supports a tooltip. */
function dropTooltip(node: DOMNode): React.ReactElement | undefined {
  if (node.type !== "tag" || node.name !== "tooltip") {
    return undefined;
  }
  return <></>;
}

/**
 * A CMS string as a reader SEES it, for a component that owns the element the
 * copy sits in and so cannot hand it to `Text` — a tab's label, a link's
 * wording. The SAME parse `Text` renders, so an authored `<sup>®</sup>` draws
 * the element in both places rather than printing its tags in one of them. A
 * string with no markup is handed straight back, so nothing moves for the copy
 * that has none.
 *
 * What it draws reads back through `plainText` as the same words, which is the
 * invariant a label depends on — the `<tooltip>` tag is the one node where the
 * two could differ, and `dropTooltip` is why they do not.
 *
 * ⚠ TRUSTED INPUT, for the reason `parseHtml` gives above. Every caller widens
 * the surface that warning describes to one more authored field.
 */
export function richText(value: string): React.ReactNode {
  if (!hasMarkup(value)) {
    return value;
  }
  /* The replace runs on the PARSED node, not on the string: the parser closes
     an unclosed `<tooltip>` at the end of the copy, so a string-level strip
     would leave that one drawn while `plainText` already reads it as nothing. */
  return parse(normalizeTooltipTags(value), { replace: dropTooltip });
}

/**
 * A CMS string as a reader hears it: the SAME parse `Text` renders, reduced to
 * its text the way an accessible name is computed — every entity decoded once,
 * a `<br>` or a block boundary read as a space, an `<img>` read as its alt, an
 * `aria-hidden` subtree skipped, whitespace collapsed. For an accessible name
 * derived from a string `Text` also draws, so the two cannot disagree
 * (WCAG 2.5.3). A string with no markup is Text's own verbatim string, with
 * only its whitespace collapsed.
 */
export function plainText(value: string): string {
  const read = hasMarkup(value) ? textOf(parseHtml(value)) : value;
  // Nothing but whitespace, a no-break space included, is no words at all.
  if (read.trim() === "") {
    return "";
  }
  // HTML whitespace only: \s and trim() would turn a no-break space into a plain one.
  return read.replace(/[ \t\n\r\f]+/g, " ").replace(/^ | $/g, "");
}

function textOf(node: React.ReactNode): string {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(textOf).join("");
  }
  if (React.isValidElement(node)) {
    const props = node.props as {
      "aria-hidden"?: boolean | string;
      alt?: string;
      children?: React.ReactNode;
      hidden?: boolean | string;
    };
    /* ⚠ TEXT IN THE TREE THAT NOBODY HEARS. An `aria-hidden` subtree or one
       hidden from the page at all; the two elements whose text is code rather
       than content; `template`, inert until cloned; and `noscript`, which only
       renders where script never ran. A name built by joining every string in
       the tree announces all of them.
       ⚠ `hidden` IS BOOLEAN HERE: the parser normalises every written form —
       bare, `=""` and `="hidden"` — to `true`. `aria-hidden` is the mirror
       image: not a boolean attribute, so only `"true"` hides it per ARIA, and
       a bare one is false and IS announced. */
    if (
      props["aria-hidden"] === true ||
      props["aria-hidden"] === "true" ||
      props.hidden === true ||
      node.type === "script" ||
      node.type === "style" ||
      node.type === "template" ||
      node.type === "noscript"
    ) {
      return "";
    }
    /* Its words are the pop-up, not the sentence: the visible text has none. */
    if (node.type === "tooltip") {
      return "";
    }
    if (node.type === "br") {
      return " ";
    }
    if (node.type === "img") {
      return ` ${props.alt ?? ""} `;
    }
    const inner = textOf(props.children);
    /* A block element is its own line to a reader: "<div>Stream</div><div>more</div>"
       is two words, not "Streammore". */
    return typeof node.type === "string" && !PHRASING.has(node.type) ? ` ${inner} ` : inner;
  }
  return "";
}

/**
 * Typographic primitive. A Server Component, and rich text renders on the
 * server like everything else. Only a `<tooltip>` tag in the copy draws a
 * client component, the Tooltip.
 */
function TextImpl({
  as,
  variant = "body-1",
  tone = "primary",
  weight,
  id,
  className,
  replace,
  children
}: TextProps) {
  /* Rich text is DETECTED, not declared: a CMS string either carries markup or
     it does not, and a flag beside it is only a second thing to get wrong. */
  const markup = typeof children === "string" && hasMarkup(children);
  const block = markup && needsBlockHost(children as string);

  /* Block content forces a div, OVERRIDING `as`. Every other element this
     component offers is phrasing-only, so the browser closes them at the first
     block child and hydration then fails against a DOM the server never
     produced. Inline-only markup keeps the element asked for. */
  const Tag = block ? "div" : as ?? "p";

  /* GLOBAL CLASS NAMES, NOT A MODULE LOOKUP. The type scale lives in
     tokens/typography-classes.css so a component can reach it without this
     atom — the same shape as .dds-sr-only and .dds-grid. */
  const classes = [
    "dds-text",
    `dds-${variant}`,
    `dds-tone-${tone}`,
    markup ? "dds-richText" : null,
    /* Wins on CSS source order — .dds-weight-* stays last in that file. */
    weight ? `dds-weight-${weight}` : null,
    className
  ]
    .filter(Boolean)
    .join(" ");

  /* The built-in tooltip draw runs first; every other node goes to the caller's
     `replace`, if any. With neither, the parse is the plain one. */
  function replaceNode(node: DOMNode, index: number) {
    return replaceTooltip(node) ?? replace?.(node, index);
  }
  const body = markup ?
    (replace || TOOLTIP_TAG.test(children as string) ?
      parse(normalizeTooltipTags(children as string), { replace: replaceNode }) :
      parse(children as string)) :
    children;

  /* <s> marks the content up as well as painting it — text-decoration alone is
     invisible to a screen reader, which would announce two live prices. */
  return (
    <Tag className={classes} id={id}>
      {variant === "number-strikethrough" ? <s>{body}</s> : body}
    </Tag>
  );
}
export const Text = withSafeRender(TextImpl, "Text");

export default Text;
